// NexusTable/createNexusFootersData.js

import { addPaddingRightToLastCell, calcStats, footCell } from "../../modules/table.funcs"
import { formatInteger, formatFloat, nundefined, yearsToString } from "../../modules/utilities"
import { COLUMN, FORMATTING, JARGON, TENSE_ENUM } from "../../modules/constants"

const { border1, left } = FORMATTING
const borderLeft = border1

export const createNexusFootersData = props => {
  // console.log('createNexusFootersData props:', props)
  calcAllStats(props.stats)
  return {
    totals: createFooterTotals(props),
    means: createFooterMeans(props),
  }
}

const calcAllStats = stats => {
  calcStats(stats.tenure)
  calcStats(stats.age)
  calcStats(stats.compensation)
  calcStats(stats.sex)
}

const createFooterTotals = ({ isCompany, lseCompany, maxLseData, portrait, showSecondDegree, showIndex, stats, tense, the350club }) => {
  const ageTotalFoot = footCell({
    borderLeft,
    id: COLUMN.AGE,
    label: formatInteger(stats.age.total)
  })
  const compensationTotalFoot = footCell({
    id: COLUMN.COMPENSATION,
    label: stats.compensation.total > 0
      ? formatInteger(stats.compensation.total)
      : JARGON.UNKNOWN,
  })

  const endTotalFoot = footCell({ id: COLUMN.UNTIL, label: "Total:" })
  const iconFoot = footCell({ id: COLUMN.ICON })
  const indexFoot = footCell({ id: COLUMN.LSE_INDEX })
  const nameFootText = stats.numRows === 1
    ? showSecondDegree
      ? isCompany ? JARGON.CONNECTED_CO : JARGON.CODIRECTOR
      : JARGON.ROLE // isCompany ? JARGON.PERSON : JARGON.COMPANY
    : showSecondDegree
      ? isCompany ? JARGON.CONNECTED_COS : JARGON.CODIRECTORS // JARGON.CODIRECTORS
      : JARGON.ROLES // isCompany ? JARGON.PEOPLE : JARGON.COMPANIES

  const nameFoot = footCell({
    // id is only used for 'key', but safer to use matching and footer ids
    id: showSecondDegree
      ? isCompany ? COLUMN.CONNECTED_CO : COLUMN.CODIRECTOR
      : isCompany ? COLUMN.OFFICER : COLUMN.COMPANY,
    align: left,
    label: `${stats.numRows} ${nameFootText}`
  })

  const overlapTotalFoot = portrait
    ? footCell({ id: COLUMN.OVERLAP, label: `Avg.: ${yearsToString(stats.tenure.total)}` })
    : footCell({ id: COLUMN.OVERLAP, label: yearsToString(stats.tenure.total) })

  const overlapMeanFoot = footCell({ id: COLUMN.OVERLAP, label: `Avg: ${yearsToString(stats.tenure.mean)}` })
  const roleFoot = footCell({ id: COLUMN.ROLE }) // `${stats.numRoles} ${stats.numRoles === 1 ? JARGON.ROLE : JARGON.ROLES}`})
  const sexTotalFoot = footCell({ id: COLUMN.SEX, label: `${formatInteger(stats.sex.total)} F` })
  const showEndColumn = tense[TENSE_ENUM.PAST] || tense[TENSE_ENUM.NOTICE]
  const starFoot = footCell({ id: COLUMN.STAR })
  const startTotalFoot = footCell({
    borderLeft,
    id: COLUMN.FROM,
    label: showEndColumn ? "" : "Total:",
  })
  const viaFoot = footCell({ id: isCompany ? COLUMN.VIA_PERSON : COLUMN.VIA_COMPANY })

  let totals = [starFoot, iconFoot, nameFoot]

  if (portrait)
    totals = [...totals, indexFoot, overlapMeanFoot]
  else {
    // Add "roleFoot"
    if (!showSecondDegree)
      totals = [...totals, roleFoot]

    // Add empty for ftse index
    if (showIndex)
      totals = [...totals, indexFoot]

    // If showSecondDegree add "viaFoot"
    if (showSecondDegree)
      totals = [...totals, viaFoot]

    // Add "startTotalFoot"
    totals = [...totals, startTotalFoot]

    // If showEndColumn add "endTotalFoot"
    if (showEndColumn)
      totals = [...totals, endTotalFoot]

    // Add "overlapTotalFoot"
    totals = [...totals, overlapTotalFoot]

    if (isCompany && !showSecondDegree)
      totals = (lseCompany && maxLseData)
        ? [...totals, compensationTotalFoot, ageTotalFoot, sexTotalFoot]
        : [...totals, ageTotalFoot]

    if (!isCompany && maxLseData && !showSecondDegree)
      totals.push(compensationTotalFoot)
  }

  if (the350club)
    totals.shift() // Remove the starHead

  addPaddingRightToLastCell(totals)
  return totals
}

const createFooterMeans = ({ isCompany, lseCompany, maxLseData, showIndex, showSecondDegree, stats, tense, the350club }) => {
  const starFoot = footCell({ id: COLUMN.STAR })
  const iconFoot = footCell({ id: COLUMN.ICON })
  const nameFootEmpty = footCell({ id: COLUMN.NAME })
  const indexFoot = footCell({ id: COLUMN.LSE_INDEX })
  const showEndColumn = tense[TENSE_ENUM.PAST] || tense[TENSE_ENUM.NOTICE]
  const percentFemale = stats.sex && !nundefined(stats.sex.mean) ? 100 * stats.sex.mean : undefined
  const roleFootEmpty = footCell({ id: COLUMN.ROLE })
  const viaFoot = footCell({ id: isCompany ? COLUMN.VIA_PERSON : COLUMN.VIA_COMPANY })
  const startMeanFoot = footCell({
    borderLeft,
    id: COLUMN.FROM,
    label: showEndColumn ? "" : "Average:",
  })
  const endMeanFoot = footCell({ id: COLUMN.UNTIL, label: "Average:" })
  const overlapMeanFoot = footCell({ id: COLUMN.OVERLAP, label: yearsToString(stats.tenure.mean) })
  const compensationMeanFoot = footCell({
    id: COLUMN.COMPENSATION,
    label: formatInteger(stats.compensation.mean),
  })
  const ageMeanFoot = footCell({
    borderLeft,
    id: COLUMN.AGE,
    label: formatFloat(stats.age.mean, 1)
  })
  const sexMeanFoot = footCell({ id: COLUMN.SEX, label: `${formatFloat(percentFemale, 1)}% F` })

  let means = [starFoot, iconFoot, nameFootEmpty]

  if (!showSecondDegree)
    means = [...means, roleFootEmpty]

  // Add "indexFoot"
  if (showIndex)
    means = [...means, indexFoot]

  // If showSecondDegree add "viaFoot"
  if (showSecondDegree)
    means = [...means, viaFoot]

  // Add "startMeanFoot"
  means = [...means, startMeanFoot]

  // if showEndColumn add "endMeanFoot"
  if (showEndColumn)
    means = [...means, endMeanFoot]

  // Add "overlapMeanFoot"
  means = [...means, overlapMeanFoot]

  if (isCompany && !showSecondDegree)
    means = (lseCompany && maxLseData)
      ? [...means, compensationMeanFoot, ageMeanFoot, sexMeanFoot]
      : [...means, ageMeanFoot]

  if (!isCompany && maxLseData && !showSecondDegree)
    means.push(compensationMeanFoot)

  if (the350club)
    means.shift() // Remove the starHead

  addPaddingRightToLastCell(means)
  return means
}

